/* global showError */

import axios from 'axios'
import { authStorage } from './authStorage'
import authConfig from 'src/configs/auth'
import { useToast } from 'src/@core/components/custom-Toast/customToast'
const getAvailableEndpoints = () => {
  return [
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_1,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_2,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_3,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_4,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_5
  ].filter(url => url && !url.startsWith('wss://'))
}

const getBaseURL = () => {
  const endpoints = getAvailableEndpoints()
  return endpoints.length > 0 ? endpoints[0] : process.env.NEXT_PUBLIC_API_URL
}

const createAPIClient = (logout, showError) => {
  const api = axios.create({
    baseURL: getBaseURL(),
    headers: {
      'Content-Type': 'application/json'
    }
  })
  api.interceptors.request.use(
    config => {
      const storedToken = authStorage.getStoredToken(authConfig.storageTokenKeyName)
      if (storedToken) {
        config.headers['Authorization'] = storedToken
      }
      return config
    },
    error => Promise.reject(error)
  )

  api.interceptors.response.use(
    response => response,
    error => {
      const capitalizeFirst = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str)
      // Opt-out for callers that render their own inline success/error status
      // (e.g. wizard Save flows) instead of the global toast. Session expiry
      // (401) always toasts regardless, since the page is about to navigate away.
      const skipGlobalToast = error?.config?.skipGlobalToast
      let message = 'Something went wrong. Please try again.'

      if (error.response) {
        let errorMessage = error?.response?.data?.message || error?.response?.data?.error
        if (errorMessage === 'segments.type.error.in.request') {
          errorMessage = 'Segment type is missing or invalid segment type'
        } else if (errorMessage === 'campaigns.audience.segment.id.required') {
          errorMessage = 'Please select at least one audience segment.'
        } else if (errorMessage === 'campaigns.invalid.segment.id') {
          errorMessage = 'One or more selected segments are invalid. Please reselect and try again.'
        } else if (errorMessage?.includes('media: upload validation failed:')) {
          const cleanMsg = errorMessage.replace('media: upload validation failed:', '').trim()
          errorMessage = cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1)
        }

        const status = error?.response?.status
        if (
          error?.response?.data?.message?.includes(
            'duplicate key value violates unique constraint "audience_hashed_email_phone_external_id_key"'
          )
        ) {
          errorMessage = 'Audience already exists, please try with a different email or phone number.'
        } else if (
          error?.response?.data?.message?.includes('duplicate key value violates unique constraint "unique_audience"')
        ) {
          errorMessage = 'Email or phone number already present.'
        } else if (status === 401) {
          // Session expiry always toasts — never suppressed by skipGlobalToast.
          message = errorMessage || 'Your session has expired. Please log in again.'
          showError(capitalizeFirst(message))
          logout()
          // authStorage.clearAuth?.()
          // window.location.href = '/login'
        } else if (status === 403) {
          message = errorMessage || 'You do not have permission to perform this action.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else if (status === 404) {
          message = errorMessage || 'The requested resource was not found.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else if (status === 400) {
          message = errorMessage || 'Bad request. Please check your input and try again.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else if (status >= 500) {
          message = errorMessage || 'Server error. Please try again later.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else {
          message = errorMessage || 'Error'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        }
      } else if (error.request) {
        message = 'Network error. Please check your connection.'
        if (!skipGlobalToast) {
          showError(capitalizeFirst(message))
        }
      }

      return Promise.reject(error)
    }
  )

  return api
}

export const useAPI = () => {
  const { showError } = useToast()
  const logout = () => {
    authStorage.clearAuth?.()
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('logout', Date.now().toString())
      window.localStorage.setItem('showLogoutToast', 'true')
      try {
        window.location.href = '/login'
      } catch {
        // Safe catch for jsdom test environments where navigation is not implemented
      }
    }
  }

  return createAPIClient(logout, showError)
}
export function handleApiError(error, fallbackMessage = 'Something went wrong.') {
  // If interceptor normalized error, use it
  const normalizedMessage =
    error?.normalizedMessage || error?.response?.data?.message || error?.message || fallbackMessage

  showError(normalizedMessage)

  return normalizedMessage
}
